import { useState, useEffect, type ReactNode } from 'react'
import { Package } from 'lucide-react'
import { cn } from '@/shared/lib/utils'

export interface ImageProps {
  /** Full-resolution image URL. When absent, the fallback is rendered. */
  src?: string | null
  /**
   * Low-resolution thumbnail URL (e.g. ``UserUploadedFile.thumbnail_url``).
   * Painted blurred underneath the full image so there is an instant,
   * meaningful preview instead of a blank box - the full image fades in on
   * top once it has finished decoding.
   */
  thumbnailSrc?: string | null
  /** Accessible description. Never rendered as visible text while loading. */
  alt: string
  /** Classes for the outer box (size, radius, border, background). */
  className?: string
  /** Object-fit behaviour for the image itself. Defaults to ``cover``. */
  fit?: 'cover' | 'contain'
  /** Rendered when there is no ``src`` or the image fails to load. */
  fallback?: ReactNode
  /** Native loading hint. Defaults to ``lazy``. */
  loading?: 'lazy' | 'eager'
  testId?: string
}

/**
 * Shared image primitive with a blur-up (LQIP) loading strategy.
 *
 * The full image is held at ``opacity-0`` until it has decoded, so its ``alt``
 * text never flashes as raw copy in the gap before the bytes arrive - the box
 * shows the blurred thumbnail (or its background colour) instead. A failed load
 * swaps in the fallback rather than the browser's broken-image glyph.
 *
 * This is the single source of truth for rendering uploaded imagery; callers
 * provide the box sizing/background via ``className`` and drop their image URLs
 * in. Prefer this over a raw ``<img>`` for any product image, avatar, or logo.
 */
export function Image({
  src,
  thumbnailSrc,
  alt,
  className,
  fit = 'cover',
  fallback,
  loading = 'lazy',
  testId,
}: ImageProps) {
  const [loaded, setLoaded] = useState(false)
  const [errored, setErrored] = useState(false)

  // Reset the blur-up transition when the source changes (e.g. navigating
  // between records or a reused list row) so a new image fades in cleanly
  // instead of inheriting the previous image's loaded/errored state.
  useEffect(() => {
    setLoaded(false)
    setErrored(false)
  }, [src])

  const fitClass = fit === 'contain' ? 'object-contain' : 'object-cover'
  const showFallback = !src || errored

  return (
    <div
      data-testid={testId}
      className={cn(
        'relative flex items-center justify-center overflow-hidden',
        className,
      )}
    >
      {showFallback ? (
        // Distinguish an explicit `null` (render nothing) from an omitted
        // prop (render the default icon) - `??` would collapse both to the
        // default.
        fallback !== undefined ? (
          fallback
        ) : (
          <Package className="h-8 w-8 text-[var(--ink-3)] opacity-30" />
        )
      ) : (
        <>
          {thumbnailSrc && !loaded && (
            <img
              src={thumbnailSrc}
              alt=""
              aria-hidden="true"
              className={cn(
                'absolute inset-0 h-full w-full scale-110 blur-md',
                fitClass,
              )}
            />
          )}
          <img
            src={src}
            alt={alt}
            loading={loading}
            decoding="async"
            onLoad={() => setLoaded(true)}
            onError={() => setErrored(true)}
            className={cn(
              'h-full w-full transition-opacity duration-300',
              fitClass,
              loaded ? 'opacity-100' : 'opacity-0',
            )}
          />
        </>
      )}
    </div>
  )
}
